/* SpawnAnything — mode "Verteidigung": the enemy waves and the economy. Pure data + validator (no imports).
   Coordinates are metres on the battlefield: the player (team 0) defends the core on the -x side, the waves come from
   the red side (+x) and later also from the north (-z) and south (+z) edges.

   The sim has exactly one army per team, so every wave is ONE unit type (team 1 switches its type between waves).
   Wave shape:
     { name,                                  shown in the HUD ("Welle 3: Legionäre")
       army: { type, count, weapon, size?, hpMul? },   hpMul scales the unit's hit points (boss balancing)
       bounty,                                gold per kill
       bonus,                                 gold when the wave is cleared
       from: ['ost' | 'nord' | 'sued', ...],  edges the wave enters from (units are dealt round-robin)
       spawnSeconds }                         all units appear within this time */

/** spawn edges: centre and half length of the line the units appear on */
export const WAVES_SIDES = {
  ost: { x: 45, z: 0, alongX: false, half: 35 },
  nord: { x: 0, z: -60, alongX: true, half: 30 },
  sued: { x: 0, z: 60, alongX: true, half: 30 },
};

export const WAVES_CONFIG = {
  startGold: 600,
  firstBuildSeconds: 40,         // countdown before wave 1
  buildSeconds: 25,              // countdown between waves
  earlyGoldPerSecond: 4,         // "Welle jetzt starten": bonus per second left on the countdown
  core: { key: 'festung', x: -55, z: 0, rot: 1 },   // the base; front (+z local) faces the red side
  defender: { count: 20, size: 1 },                 // starting army of the player (type + weapon from "Armee Blau")
  area: [-110, 20, -45, 45],     // build zone [x0, x1, z0, z1]: whole plot must fit inside
  maxBuilt: 60,                  // buildings bought per game
};

const A = (type, count, weapon, size = 1, hpMul = 1) => ({ type, count, weapon, size, hpMul });

export const WAVES = [
  { name: 'Hühner', army: A('huhn', 80, 'faeuste'), bounty: 3, bonus: 150, from: ['ost'], spawnSeconds: 6 },
  { name: 'Zombies', army: A('zombie', 100, 'faeuste'), bounty: 4, bonus: 200, from: ['ost'], spawnSeconds: 8 },
  { name: 'Legionäre', army: A('legionaer', 100, 'schwert'), bounty: 6, bonus: 250, from: ['ost', 'nord'], spawnSeconds: 10 },
  { name: 'Soldaten', army: A('soldat', 120, 'gewehr'), bounty: 6, bonus: 300, from: ['ost', 'sued'], spawnSeconds: 10 },
  { name: 'Mech-Hasen', army: A('mechhase', 30, 'huhnkanone'), bounty: 30, bonus: 350, from: ['ost', 'nord', 'sued'], spawnSeconds: 10 },
  { name: 'Panzer', army: A('panzer', 16, 'kanone'), bounty: 60, bonus: 400, from: ['ost', 'nord', 'sued'], spawnSeconds: 10 },
  { name: 'T-Rex', army: A('trex', 5, 'faeuste'), bounty: 200, bonus: 500, from: ['ost', 'nord', 'sued'], spawnSeconds: 8 },
  { name: 'Boss: Seemonster', army: A('seemonster', 1, 'faeuste', 1, 0.5), bounty: 2000, bonus: 0, from: ['ost'], spawnSeconds: 1 },
];

/** the largest wave (= reserved unit slots of team 1) */
export const wavesMaxCount = (waves = WAVES) => waves.reduce((m, w) => Math.max(m, w.army.count), 1);

/**
 * Check a wave list against the unit/weapon tables.
 * @param {object[]} waves
 * @param {{ types?: object, weapons?: object }} [tables]  TYPES / WEAPONS (optional: skips the lookups)
 * @returns {string[]} problems (empty = ok)
 */
export function validateWaves(waves, { types, weapons } = {}) {
  const errs = [];
  if (!Array.isArray(waves) || !waves.length) return ['waves: empty'];
  waves.forEach((w, i) => {
    const p = `waves[${i}]`;
    if (typeof w.name !== 'string' || !w.name) errs.push(`${p}.name missing`);
    const a = w.army || {};
    if (types && !types[a.type]) errs.push(`${p}.army.type unknown: ${a.type}`);
    if (weapons && !weapons[a.weapon]) errs.push(`${p}.army.weapon unknown: ${a.weapon}`);
    if (!Number.isInteger(a.count) || a.count < 1 || a.count > 2000) errs.push(`${p}.army.count out of range`);
    if (!(a.size >= 1 && a.size <= 20)) errs.push(`${p}.army.size out of range`);
    if (!(a.hpMul > 0 && a.hpMul <= 10)) errs.push(`${p}.army.hpMul out of range`);
    for (const k of ['bounty', 'bonus']) if (!Number.isInteger(w[k]) || w[k] < 0) errs.push(`${p}.${k} must be a whole number >= 0`);
    if (!Array.isArray(w.from) || !w.from.length || w.from.some(s => !WAVES_SIDES[s])) errs.push(`${p}.from invalid`);
    if (!(w.spawnSeconds > 0 && w.spawnSeconds <= 60)) errs.push(`${p}.spawnSeconds out of range`);
  });
  return errs;
}
